/* eslint-disable import/order */
import * as Sentry from '@sentry/react';

import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';

import { NavBarLayout } from './NavBar';
import { DirectSurveyAutoAuth } from './direct/DirectSurveyAutoAuth';
import { DirectProtectedComponent } from './direct/DirectProtectedComponent';

import { AutoAuth } from '@/components/login/AutoAuth';
import { Login } from '@/components/login/Login';
import { PasswordResetForm } from '@/components/login/PasswordResetForm';
import { Setup } from '@/components/login/Setup';
import { SignupAccess } from '@/components/login/SignupAccess';
import { TaskAuth } from '@/components/login/TaskAuth';
import { Verify } from '@/components/login/Verify';
import { ConfirmProfile } from '@/components/home/ConfirmProfile';

import { Home } from '@/components/home/Home';
import { AppointmentList } from '@/components/home/appointments/AppointmentList';

import { TodoIndex } from '@/components/todo/TodoIndex';

import { Education } from '@/components/education/Education';

import { Inbox } from '@/components/inbox/Inbox';

import { Settings } from '@/components/settings/Settings';

import { SurveyProvider } from '@/components/survey/SurveyProvider';
import { Result } from '@/components/survey/result/Result';
import { ResultCharts } from '@/components/survey/result/ResultCharts';
import { OpioidResult } from '@/components/survey/opioid/OpioidResult';

import { ProviderExit } from '@/components/survey/layout/ProviderExit';
import { DirectExit } from '@/components/survey/layout/DirectExit';

import { ProgressTabs } from '@/components/progress/ProgressTabs';
import { MetricChart } from '@/components/progress/metrics/MetricChart';
import { ChartsList } from '@/components/progress/charts/ChartsList';
import { TrackerList } from '@/components/progress/trackers/TrackerList';
import { TrackerTabs } from '@/components/progress/trackers/TrackerTab';

import { NoMatch } from '@/components/layout/NoMatch';

import useSegment from '@/hooks/useSegment';
import useSetupPrompt from '@/hooks/useSetupPrompt';
import { useAppSelector } from '@/redux/hooks';
import { getURLParam } from '@/patient-app-common/utils';

import { Demo } from '@/components/demo/Demo';
import { JsonView } from '@/components/demo/JsonView';

const VALID_ROUTES = [
  '/login',
  '/home',
  '/appointments',
  '/todos',
  '/education/:componentId?',
  '/progress',
  '/progress/surveys/:trackerId?/',
  '/progress/metrics/:trackerId',
  '/inbox',
  '/profile',
  '/demo',
  '/tracker/:surveyId?',
  '/recommendation/charts/:surveyId',
  '/recommendation/:surveyId',
  '/opioidrisk',
];

const autoAuthRoutes = [
  '/auth_auto/:token/:eventId?',
  '/provider_tracker/:token/:surveyId/:eventId?',
];

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

export function AppRouter() {
  const { providerId, admin, directMode } = useAppSelector(
    (state) => state.auth
  );
  const { pathname, search } = useLocation();

  const { trackPage } = useSegment();

  useSetupPrompt();

  useEffect(() => {
    trackPage(pathname);
  }, [pathname]);

  // Check URL param directly for direct mode to avoid race conditions
  const urlDirectMode = getURLParam(search, 'mode') === 'direct';
  // Also check Redux state to persist direct mode across navigation
  const isDirectMode = urlDirectMode || directMode;
  const providerSurveyMode = providerId && !admin;

  if (providerSurveyMode) {
    return <ProviderSurveyRouter pathname={pathname} />;
  }

  if (isDirectMode) {
    return <DirectSurveyRouter pathname={pathname} />;
  }

  return <PatientRouter pathname={pathname} />;
}

export function PatientRouter({ pathname }: { pathname: string }) {
  const { token, mfaChecked } = useAppSelector(({ auth }) => auth);
  const authenticated = token && mfaChecked;

  return (
    <>
      <SentryRoutes>
        {autoAuthRoutes.map((path) => (
          <Route key={path} path={path} element={<AutoAuth />} />
        ))}
        <Route path="/setup/:accessKey/:setupToken" element={<Setup />} />

        {!token && (
          <>
            <Route path="/auto_tasks/" element={<TaskAuth />} />
            <Route path="/" element={<Login />} />
            <Route
              path="/reset_password_form/:token?"
              element={<PasswordResetForm />}
            />
            <Route
              path="/request_password"
              element={<Navigate to="/reset_password_form" replace />}
            />
            <Route path="/signup/:accessKey?" element={<SignupAccess />} />
            {VALID_ROUTES.map((path) => (
              <Route
                key={path}
                path={path}
                element={<Navigate to="/" replace />}
              />
            ))}
            <Route path="*" element={<NoMatch />} />
          </>
        )}

        {token && !authenticated && <Route path="*" element={<Verify />} />}

        {authenticated && (
          <>
            {['/login', '/signup', '/signup/:accessKey'].map((path) => (
              <Route
                key={path}
                path={path}
                element={<Navigate to="/" replace />}
              />
            ))}
            <Route path="/confirm_profile" element={<ConfirmProfile />} />
            <Route path="/" element={<NavBarLayout />}>
              <Route index element={<Navigate to="/home" replace />} />
              <Route path="/home" element={<Home />} />
              <Route path="/todos" element={<TodoIndex />} />
              <Route path="/appointments" element={<AppointmentList />} />
              <Route path="/education/:topicId?" element={<Education />} />
              <Route path="/progress" element={<ProgressTabs />} />
              <Route path="/progress/surveys/charts" element={<ChartsList />} />
              <Route path="/progress/surveys" element={<TrackerList />} />
              <Route
                path="/progress/surveys/:trackerId"
                element={<TrackerTabs />}
              />
              <Route
                path="/progress/metrics/:trackerId"
                element={<MetricChart />}
              />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/profile" element={<Settings />} />
            </Route>
            <Route
              path="/tracker/:surveyId?"
              element={<SurveyProvider key={pathname} />}
            />
            <Route
              path="/recommendation/charts/:surveyId"
              element={<ResultCharts />}
            />
            <Route path="/recommendation/:surveyId" element={<Result />} />
            <Route path="/opioidrisk" element={<OpioidResult />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/json" element={<JsonView />} />
            <Route path="*" element={<NoMatch />} />
          </>
        )}
      </SentryRoutes>
    </>
  );
}

function ProviderSurveyRouter({ pathname }: { pathname: string }) {
  return (
    <SentryRoutes>
      {autoAuthRoutes.map((path) => (
        <Route key={path} path={path} element={<AutoAuth />} />
      ))}
      <Route
        path="/tracker/:surveyId?/:token?"
        key={pathname}
        element={<SurveyProvider />}
      />
      <Route path="*" element={<ProviderExit />} />
    </SentryRoutes>
  );
}

function DirectSurveyRouter({ pathname }: { pathname: string }) {
  return (
    <SentryRoutes>
      <Route
        key="/auth_auto/:token/:eventId?"
        path="/auth_auto/:token/:eventId?"
        element={<DirectSurveyAutoAuth />}
      />
      <Route
        path="/tracker/:surveyId?/:token?"
        key={pathname}
        element={<DirectProtectedComponent component={<SurveyProvider />} />}
      />
      <Route
        path="/recommendation/:surveyId"
        element={<DirectProtectedComponent component={<Result />} />}
      />
      <Route
        path="/recommendation/charts/:surveyId"
        element={<DirectProtectedComponent component={<ResultCharts />} />}
      />
      <Route
        path="*"
        element={<DirectProtectedComponent component={<DirectExit />} />}
      />
    </SentryRoutes>
  );
}
